import React from 'react';
import SectionHeading from './shared/SectionHeading';
import TrustBar from './home/TrustBar';
import FinalCTA from './home/FinalCTA';
import PrimaryButton from './shared/PrimaryButton';
import SecondaryButton from './shared/SecondaryButton';
import serviceAreas from '../data/serviceAreas';
import {
  BUSINESS_LOCATION,
  EMERGENCY_HOURS,
  FOUNDED_YEAR,
  HVAC_LICENSE,
  OWNER_NAME,
  PHONE_NUMBER,
  PHONE_NUMBER_LINK,
  PRIMARY_CTA_TEXT,
  REGULAR_HOURS,
  SECONDARY_CTA_TEXT,
  SERVICE_AREA_SUMMARY,
} from '../data/contact';
import heroImage from '../assets/about-hero-optimized.jpg';
import mountainImage from '../assets/about-service-area-optimized.jpg';

const companyPoints = [
  {
    title: 'Family-owned since 2003',
    description:
      'Preferred Service Heating, Cooling & Air is a small local company built around dependable long-term service.',
  },
  {
    title: 'Clear recommendations',
    description:
      'Homeowners count on transparent pricing, honest conversations, and a no-pressure approach to HVAC decisions.',
  },
  {
    title: 'Residential and commercial experience',
    description:
      'The team supports heating, cooling, and air purification needs with practical recommendations sized to the job.',
  },
  {
    title: 'Known around South Jordan',
    description:
      'Being local keeps communication direct and helps service feel more consistent over time.',
  },
];

const credentials = [
  `Located in ${BUSINESS_LOCATION}`,
  `Serving ${SERVICE_AREA_SUMMARY}`,
  `Founder and owner: ${OWNER_NAME}`,
  `Serving residential and commercial clients since ${FOUNDED_YEAR}`,
  `Utah HVAC Qualifier license #${HVAC_LICENSE}`,
];

const About = () => (
  <main>
    <section className="about-page-hero section">
      <div className="container about-page-hero-grid">
        <div className="about-page-hero-copy">
          <p className="section-eyebrow">About Preferred Service</p>
          <h1>Local heating and cooling service built on trust, clarity, and long-term customer care.</h1>
          <p className="about-page-hero-text">
            Preferred Service Heating, Cooling & Air is a family-owned company based in South
            Jordan, Utah. Since 2003, the focus has stayed the same: dependable HVAC support,
            practical recommendations, and service that treats homeowners with respect.
          </p>

          <div className="hero-actions">
            <PrimaryButton to="/contact">{PRIMARY_CTA_TEXT}</PrimaryButton>
            <SecondaryButton href={PHONE_NUMBER_LINK} aria-label={`Call Now at ${PHONE_NUMBER}`}>
              {SECONDARY_CTA_TEXT}
            </SecondaryButton>
          </div>
        </div>

        <div className="about-page-hero-side">
          <div className="about-page-hero-image">
            <img
              src={heroImage}
              alt="Preferred Service equipment and technician work area"
              decoding="async"
              fetchPriority="high"
            />
          </div>
          <div className="card about-page-hero-panel">
            <p className="section-eyebrow">Company Snapshot</p>
            <ul className="about-page-detail-list">
              <li>Family-owned HVAC company in South Jordan</li>
              <li>Operating locally since 2003</li>
              <li>Heating, cooling, and air purification support</li>
              <li>Transparent pricing and no-pressure service</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <TrustBar />

    <section className="section">
      <div className="container why-choose-us">
        <div>
          <SectionHeading
            eyebrow="Who We Are"
            title="A smaller local company with a service style built around direct communication."
            description="You work with a local team that explains the issue, discusses practical options, and treats long-term customer relationships as part of the job."
          />
        </div>
        <div className="responsive-grid responsive-grid-2">
          {companyPoints.map((point) => (
            <article key={point.title} className="card value-card">
              <h3>{point.title}</h3>
              <p>{point.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="section section-surface">
      <div className="container about-story-layout">
        <div>
          <SectionHeading
            eyebrow="Credentials"
            title="Experience and local roots that help customers feel confident in the people behind the work."
            description="The company story stays simple: local ownership, years of experience, and a reputation built on honest service rather than heavy sales pressure."
          />
          <div className="card about-credentials-card">
            <ul className="about-page-detail-list">
              {credentials.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="about-service-area">
          <div className="service-area-image card">
            <img
              src={mountainImage}
              alt="Wasatch mountain view near the Salt Lake Valley"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="card about-hours-card">
            <p className="section-eyebrow">Hours Of Operation</p>
            <div className="about-hours-row">
              <span>Monday - Friday</span>
              <strong>{REGULAR_HOURS.replace('Monday - Friday: ', '')}</strong>
            </div>
            <div className="about-hours-row">
              <span>Emergency Service</span>
              <strong>{EMERGENCY_HOURS.replace('Emergency service ', '')}</strong>
            </div>
            <div className="service-area-list">
              {serviceAreas.map((area) => (
                <span key={area} className="service-area-pill">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <FinalCTA />
  </main>
);

export default About;
